<template>
    <v-data-table
        ref="dataTable"
        item-key="MEMB_N"
        class="memberQualificationTbl"
        :loading="loading ? 'teal darken-1' : null"
        :items="loading ? [] : items"
        :items-per-page.sync="itemsPerPage"
        :headers="headers"
        :page.sync="pageNumber"
        :search="search"
        :footer-props="footerProps"
        @pagination="scrollToTop"
        @current-items="currentItems = $event"
    >
        <!-- Header: checkbox selects the current page, button selects all pages -->
        <template #[`header.data-table-select`]>
            <th>
                <div class="selectHeader">
                    <v-tooltip
                        top
                        :disabled="controlsDisabled"
                    >
                        <template #activator="{ on: tooltipOn }">
                            <v-simple-checkbox
                                class="selectHeader__page"
                                color="teal"
                                :disabled="disabled || loading || selectablePageRows.length === 0"
                                :value="isPageSelected"
                                :indeterminate="isPageIndeterminate"
                                @input="togglePage"
                                v-on="tooltipOn"
                            />
                        </template>
                        <span>{{ pageTooltipLabel }}</span>
                    </v-tooltip>
                    <v-tooltip
                        top
                        :disabled="controlsDisabled"
                    >
                        <template #activator="{ on: tooltipOn }">
                            <v-btn
                                class="selectHeader__all"
                                icon
                                dense
                                color="teal"
                                :disabled="disabled || loading"
                                v-on="tooltipOn"
                                @click="toggleAll"
                            >
                                <v-icon>{{ isAllSelected ? mdiSelectRemove : mdiSelectAll }}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ allTooltipLabel }}</span>
                    </v-tooltip>
                </div>
            </th>
        </template>

        <!-- Rows -->
        <template #item="{ item }">
            <tr>
                <td
                    v-for="header in headers"
                    :key="header.value"
                    :class="{ 'text-center': centeredColumns.includes(header.value) }"
                >
                    <span class="nowrap">
                        <template v-if="dateFields.includes(header.value)">
                            {{ item[header.value] ? formatDateToPst(item[header.value]) : '' }}
                        </template>
                        <template v-else-if="header.value === 'QUALIFIED'">
                            <v-tooltip top>
                                <template #activator="{ on }">
                                    <v-icon
                                        :color="item.QUALIFIED ? 'success' : 'error'"
                                        v-on="on"
                                    >
                                        {{ item.QUALIFIED ? mdiCheckCircle : mdiCloseCircle }}
                                    </v-icon>
                                </template>
                                <span>{{ item.QUALIFIED ? 'Qualified' : 'Disqualified' }}</span>
                            </v-tooltip>
                        </template>
                        <template v-else-if="header.value === 'REMARKS'">
                            <ul>
                                <li
                                    v-for="(remark, i) in item.REMARKS"
                                    :key="i"
                                    class="nowrap text-left"
                                >{{ remark }}</li>
                            </ul>
                        </template>
                        <template v-else-if="header.value === 'LAST_M'">
                            {{ item.LAST_M }} {{ item.MIDI_M ? `${item.MIDI_M[0]}.` : '' }}
                        </template>
                        <template v-else-if="header.value === 'MEMB_N'">
                            <span class="memberNumber">{{ item.MEMB_N }}</span>
                        </template>
                        <template v-else-if="header.value === 'data-table-select'">
                            <v-simple-checkbox
                                v-if="isRowSelectable(item) || isExistingMember(item)"
                                class="rowCheckbox"
                                color="teal"
                                :value="isExistingMember(item) || isRowChecked(item)"
                                :disabled="isExistingMember(item)"
                                @input="toggleRow(item)"
                            />
                        </template>
                        <template v-else>
                            {{ item[header.value] }}
                        </template>
                    </span>
                </td>
            </tr>
        </template>
    </v-data-table>
</template>

<script>
import { moment } from '../../../utils/date-shim'
import {
    mdiSelectAll,
    mdiSelectRemove,
    mdiCloseCircle,
    mdiCheckCircle
} from '@mdi/js'

export default {
    name: 'MemberQualificationTable',
    props: {
        // v-model: array of selected rows
        value: {
            type: Array,
            default: () => []
        },
        // rows to display (already filtered by tab, etc.)
        items: {
            type: Array,
            default: () => []
        },
        // when true, rows with IS_SELECTABLE === false can still be selected
        alwaysAllowSelection: {
            type: Boolean,
            default: false
        },
        // members already added; shown as checked + disabled and never emitted
        selectedMembers: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        },
        // disables the select controls (e.g. no account selected)
        disabled: {
            type: Boolean,
            default: false
        },
        // search string passed to v-data-table
        search: {
            type: String,
            default: null
        }
    },
    data: () => ({
        mdiSelectAll,
        mdiSelectRemove,
        mdiCloseCircle,
        mdiCheckCircle,
        currentItems: [],
        pageNumber: 1,
        itemsPerPage: 500,
        centeredColumns: ['SEX', 'AGE', 'QUALIFIED', 'data-table-select'],
        dateFields: ['BIRT_D', 'PDUN_D', 'STRT_D', 'AEXP_D', 'LAST_D', 'GRCP_D'],
        footerProps: {
            'items-per-page-options': [10, 20, 30, 40, 50, 100, 200, 300, 400, 500, 1000, -1],
            'items-per-page-text': ''
        },
        headers: [
            { text: '', value: 'data-table-select', align: 'center', width: 30, sortable: false, class: 'no-padding-header' },
            { text: 'Member Number', value: 'MEMB_N', align: 'center' },
            { text: 'Member Name', value: 'LAST_M', align: 'center' },
            { text: 'Gender', value: 'SEX', align: 'center' },
            { text: 'Birthdate', value: 'BIRT_D', align: 'center' },
            { text: 'Age', value: 'AGE', align: 'center' },
            { text: 'Mode of Payment', value: 'PAYM_C', align: 'center' },
            { text: 'Effectivity Date', value: 'STRT_D', align: 'center' },
            { text: 'Paid Until', value: 'PDUN_D', align: 'center' },
            { text: 'Member Expiry Date', value: 'LAST_D', align: 'center' },
            { text: 'Account Expiry Date', value: 'AEXP_D', align: 'center' },
            { text: 'Grace Period', value: 'GRCP_D', align: 'center' },
            { text: 'Qualified', value: 'QUALIFIED', align: 'center' },
            { text: 'Remarks', value: 'REMARKS', align: 'center' }
        ]
    }),
    computed: {
        controlsDisabled() {
            return this.disabled || this.items.length < 1
        },
        existingMemberNumbers() {
            return new Set(this.selectedMembers.map(m => String(m.MEMB_N)))
        },
        checkedKeys() {
            return new Set(this.value.map(v => v.MEMB_N))
        },

        // rows that can be picked (all pages / current page)
        selectableRows() {
            return this.items.filter(this.isPickable)
        },
        selectablePageRows() {
            return this.currentItems.filter(this.isPickable)
        },

        // all pages (button)
        isAllSelected() {
            return this.selectableRows.length > 0 && this.selectableRows.every(this.isRowChecked)
        },
        allTooltipLabel() {
            const total = this.selectableRows.length
            return this.isAllSelected ? `Deselect all ${total}` : `Select all ${total}`
        },

        // current page (checkbox)
        isPageSelected() {
            return this.selectablePageRows.length > 0 && this.selectablePageRows.every(this.isRowChecked)
        },
        isPageIndeterminate() {
            return !this.isPageSelected && this.selectablePageRows.some(this.isRowChecked)
        },
        pageTooltipLabel() {
            const total = this.selectablePageRows.length
            return this.isPageSelected ? `Deselect ${total} on this page` : `Select ${total} on this page`
        }
    },
    methods: {
        isExistingMember(item) {
            return this.existingMemberNumbers.has(String(item.MEMB_N))
        },
        isRowSelectable(item) {
            return this.alwaysAllowSelection || !!item.IS_SELECTABLE
        },
        isPickable(item) {
            return this.isRowSelectable(item) && !this.isExistingMember(item)
        },
        isRowChecked(item) {
            return this.checkedKeys.has(item.MEMB_N)
        },

        // Select or deselect the given rows, leaving other selections untouched.
        setRows(rows, selected) {
            const keys = new Set(rows.map(r => r.MEMB_N))
            const kept = this.value.filter(v => !keys.has(v.MEMB_N))
            this.$emit('input', selected ? [...kept, ...rows] : kept)
        },
        toggleRow(item) {
            this.setRows([item], !this.isRowChecked(item))
        },
        togglePage() {
            this.setRows(this.selectablePageRows, !this.isPageSelected)
        },
        toggleAll() {
            this.setRows(this.selectableRows, !this.isAllSelected)
        },

        scrollToTop() {
            this.$nextTick(() => {
                const table = this.$refs.dataTable && this.$refs.dataTable.$el
                if (!table) return
                const target = table.querySelector('thead tr') || table
                target.scrollIntoView({ block: 'start' })
            })
        },
        formatDateToPst(date) {
            return date ? moment(date).utcOffset('+0800').format('MMMM DD, YYYY') : '-'
        }
    }
}
</script>

<style>
.no-padding-header {
    padding: 0 !important;
}
.memberQualificationTbl .nowrap {
    white-space: nowrap;
}
.memberQualificationTbl .selectHeader {
    width: 94px;
    height: 50px;
    vertical-align: top;
}
.memberQualificationTbl .selectHeader__page {
    display: inline-block;
    margin: 10px 0 0 0;
    padding: 0 7px;
    vertical-align: top;
}
.memberQualificationTbl .selectHeader__all {
    display: inline;
    margin: 4px 0 0 0;
    padding: 0;
    vertical-align: top;
}
.memberQualificationTbl .rowCheckbox {
    display: inline-block;
    margin: 12px 0 0 10px;
    vertical-align: top;
}
.memberQualificationTbl .memberNumber {
    padding: 0 30px;
}

/* Sticky header + first two columns */
.memberQualificationTbl > div > table > thead > tr > th {
    width: 100px;
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: white;
}
.memberQualificationTbl > div > table > tbody > tr > td {
    background: white;
}
.memberQualificationTbl > div > table > tbody > tr > td:nth-child(1),
.memberQualificationTbl > div > table > tbody > tr > td:nth-child(2) {
    width: 50px;
    position: sticky !important;
    padding: 0;
    z-index: 2;
}
.memberQualificationTbl > div > table > tbody > tr > td:nth-child(1),
.memberQualificationTbl > div > table > thead > tr > th:nth-child(1) {
    left: 0;
}
.memberQualificationTbl > div > table > tbody > tr > td:nth-child(2),
.memberQualificationTbl > div > table > thead > tr > th:nth-child(2) {
    left: 93px;
}
.memberQualificationTbl > div > table > thead > tr > th:nth-child(1),
.memberQualificationTbl > div > table > thead > tr > th:nth-child(2) {
    width: 50px;
    z-index: 5;
}
.memberQualificationTbl .v-data-table__wrapper {
    max-height: 70vh;
    overflow-y: auto;
}
</style>
